import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { routeUrls } from "../navigation/routeUrls";
import { TAdminTableRowData } from "./types";
import svgIcons from "../assets/svg/svgIcons";
import { APP_CONFIG } from "../constants/constants";
import { useSelector } from "react-redux";

interface AdminTableSortableProps {
  columns: string[];
  compact?: boolean;
  customTableHeadStyles?: string;
  customWrapperClass?: string;
  data: TAdminTableRowData[] | null;
  heightLimitClass?: string;
  isLoading: boolean;
  listingQueryParams?: any;
  listingWrapperRef?: React.RefObject<HTMLDivElement>;
  rightAlignLastColumn?: boolean;
  viewportHeightOffset?: "400" | "500" | "600" | null;
}

const AdminTableSortable: FC<AdminTableSortableProps> = ({
  columns,
  compact = false,
  customTableHeadStyles = "",
  customWrapperClass = "overflow-auto relative",
  data,
  heightLimitClass = "",
  isLoading = false,
  listingQueryParams = {},
  listingWrapperRef = null,
  rightAlignLastColumn = false,
  viewportHeightOffset = null,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  let wrapperHeightClass = "";
  const userIsImpersonated = useSelector(
    (state: { commonReducer: { userIsImpersonated: boolean } }) =>
      state.commonReducer.userIsImpersonated
  );
  switch (viewportHeightOffset) {
    case "400":
      // wrapperHeightClass = 'max-h-[calc(100vh-400px)]';
      wrapperHeightClass = userIsImpersonated
        ? "max-h-[calc(100vh-420px)]" // + 40px for impersonation (blue) bar
        : "max-h-[calc(100vh-380px)]"; // 48+32+52+16+40+42+24+16+16+38+28= 352 ~= 360 + 10 (10px of bottom spacing) = 370
      break;
    case "500":
      wrapperHeightClass = "max-h-[calc(100vh-500px)]"; // TODO: check later if this is being used
      break;
    case "600":
      wrapperHeightClass = "max-h-[calc(100vh-600px)]"; // TODO: check later if this is being used
      break;
    default:
      wrapperHeightClass = "";
  }
  const wrapperClass = `${customWrapperClass} ${heightLimitClass} ${wrapperHeightClass}`;
  const lastColumnAlignmentClass = rightAlignLastColumn
    ? "text-right"
    : "text-left";

  const [sortByColumn, setSortByColumn] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  /**
   * Method to handle sorting of the table
   * Column not selected yet -> apply descending sorting
   * Column selected -> apply ascending sorting
   * Column selected and already in ascending -> reset sorting
   * @param column Column of the table
   */
  const handleSort = (column: string) => {
    if (sortByColumn !== column) {
      setSortByColumn(column);
      setSortOrder("desc");
    } else if (sortByColumn === column && sortOrder === "desc") {
      setSortOrder("asc");
    } else {
      setSortByColumn(null);
      setSortOrder("asc");
    }
  };

  const sortedData = data?.slice().sort((a, b) => {
    if (!sortByColumn) return 0;
    const aValue = a.cellData[columns.indexOf(sortByColumn)];
    const bValue = b.cellData[columns.indexOf(sortByColumn)];
    if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
    if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <>
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <pre className="absolute left-1/4 text-[8px] text-field-error-dark -mt-10">
          sortByColumn: {sortByColumn}
          <br />
          sortOrder: {sortOrder}
          <br />
        </pre>
      )}
      <div className={wrapperClass} ref={listingWrapperRef}>
        <div
          className={`sticky top-1/2 z-listing-table min-w-full h-full${isLoading ? "" : " hidden"}`}
        >
          <img
            src={svgIcons.LoadingAnimation}
            alt="loading-animation"
            className="size-16 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
          />
        </div>
        <table
          className={`w-full transition border-collapse border-spacing-0${isLoading ? " opacity-25 pointer-events-none touch-none" : ""}`}
        >
          <thead
            className={`sticky top-0 bg-white border-b border-gray-500 ${customTableHeadStyles}`}
          >
            <tr>
              {columns?.map((column, index) => (
                <th
                  key={index}
                  className={`text-left text-sm font-semibold text-black leading-6 whitespace-nowrap p-0${index === columns.length - 1 ? ` ${lastColumnAlignmentClass}` : ""}`}
                >
                  <button
                    type="button"
                    className={
                      `relative flex items-center border-b-2 border-gray-400 Xhover:bg-black/5 rounded-tl-lg rounded-tr-lg py-1 w-full ` +
                      `${true ? " group cursor-pointer" : ""}`
                    } // true because we always need to show the sort icons for every column // column === sortByColumn
                    onClick={() => handleSort(column)}
                  >
                    <span
                      className={`flex items-center gap-2 rounded-lg group-hover:bg-accent-blue-paleO66 px-4 ${index === columns.length - 1 ? "Xmr-24 Xpl-3 Xpr-24" : "Xpx-4"}`}
                    >
                      <span className="text-l font-semibold text-black-500 leading-10">
                        {column}
                      </span>
                      {true && ( // true because we always need to show the sort icons for every column // column === sortByColumn
                        <span className="flex flex-col w-4">
                          {
                            // results sorted in ascending order by this column
                            column === sortByColumn && sortOrder === "asc" ? (
                              <>
                                <img
                                  src={svgIcons.CollapseIcon}
                                  alt={column}
                                  className="size-4 group-hover:grayscale group-hover:opacity-15"
                                />
                                <img
                                  src={svgIcons.ExpandMoreIcon}
                                  alt={column}
                                  className="size-4 grayscale opacity-50 group-hover:grayscale group-hover:opacity-15"
                                />
                              </>
                            ) : // results sorted in descending order by this column
                            column === sortByColumn && sortOrder === "desc" ? (
                              <>
                                <img
                                  src={svgIcons.CollapseIcon}
                                  alt={column}
                                  className="size-4 grayscale opacity-50 group-hover:grayscale-0 group-hover:opacity-100"
                                />
                                <img
                                  src={svgIcons.ExpandMoreIcon}
                                  alt={column}
                                  className="size-4 group-hover:grayscale group-hover:opacity-15"
                                />
                              </>
                            ) : (
                              // results not sorted by this column
                              <>
                                <img
                                  src={svgIcons.CollapseIcon}
                                  alt={column}
                                  className="size-4 grayscale opacity-50 group-hover:opacity-15"
                                />
                                <img
                                  src={svgIcons.ExpandMoreIcon}
                                  alt={column}
                                  className="size-4 grayscale opacity-50 group-hover:grayscale-0 group-hover:opacity-100"
                                />
                              </>
                            )
                          }
                        </span>
                      )}
                    </span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 border-b border-gray-200">
            {!!sortedData && sortedData.length > 0 ? (
              sortedData?.map((rowData: TAdminTableRowData, index: number) => {
                const rowTextColorClass =
                  rowData.isActive === false
                    ? "text-gray-700" // use 'text-gray-400' to show inactive rows in light gray
                    : "text-gray-700";
                return (
                  <tr
                    key={`row_${index}`}
                    className={`cursor-pointer hover:bg-accent-blue-paleO66`}
                    onClick={() => {
                      if (
                        !!rowData.navLink &&
                        typeof rowData.navLink !== "boolean" &&
                        !isLoading
                      ) {
                        if (typeof rowData.navLink === "function") {
                          rowData.navLink?.();
                        } else {
                          navigate(rowData.navLink, {
                            state: listingQueryParams,
                          });
                        }
                      }
                    }}
                  >
                    {rowData.cellData?.map((cellData, i) => (
                      <td
                        key={`row_${index}_cell_${i}`}
                        className={`${compact ? "px-2 py-1" : "px-4 py-3"} whitespace-nowrap text-xs font-medium leading-6 ${rowTextColorClass} border-t border-gray-200${i === rowData.cellData.length - 1 ? ` ${lastColumnAlignmentClass}` : ""}`}
                      >
                        {/* extra debugging span */}
                        {APP_CONFIG.DEBUG.DISPLAY_VERBOSE &&
                          !!rowData?.cellDataMeta?.[i] && (
                            <span className="relative">
                              <pre className="absolute bottom-full text-[8px] leading-[8px] text-black/50">
                                {rowData?.cellDataMeta?.[i]}
                              </pre>
                            </span>
                          )}

                        {/* display cell data */}
                        {cellData}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : isLoading ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center py-24 text-lg font-bold text-gray-400"
                >
                  &nbsp;
                </td>
              </tr>
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center py-24 text-lg font-bold text-gray-400"
                >
                  {t("no_items_found")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default AdminTableSortable;
